import React from "react";
import { Link } from "react-router-dom";
import { prettyCountry } from "../lib/catalog";
import { mediaUrl } from "../lib/siteApi";

const SERVICE_PACKS = {
  photovoltaic: {
    who: "Homes, farms, clinics, hotels, offices and utilities that need reliable power where the grid is weak, expensive or unavailable.",
    get: ["Site survey and load-based system sizing", "Quality panels, inverters, structure and cabling", "Off-grid, hybrid or grid-tied design", "Installation, testing and documented handover", "Maintenance contracts and operator training"],
    specs: [
      ["Typical sizes", "1 kWp rooftops to multi-hundred kWp commercial plants"],
      ["Modes", "Off-grid, on-grid, hybrid with storage"],
      ["Life of plant", "20–25 years with scheduled service"],
    ],
    faqs: [
      ["How long do RENERG solar systems last?", "A well-designed photovoltaic system typically delivers 20–25 years of production with scheduled maintenance."],
      ["Can we mix solar with the national grid?", "Yes. We install on-grid and hybrid systems so you use solar first and the grid as backup."],
      ["Do you work outside Rwanda?", "Yes. Filter by country on the Projects page — including new markets such as Canada."],
    ],
  },
  realestate: {
    who: "Developers, hotels, churches and families who want buildings that cost less to run and sit well in Rwanda’s green-city agenda.",
    get: ["Architectural concept and energy modelling", "Green materials and insulation choices", "On-site solar and water efficiency", "Building automation and comfort control", "Construction management to handover"],
    specs: [
      ["Focus", "Lower bills, comfort and a smaller carbon footprint"],
      ["Works on", "New build and retrofit"],
      ["Integrates", "Solar, lighting, water and controls"],
    ],
    faqs: [
      ["Is a green building more expensive?", "Upfront cost can be similar; the saving comes from lower electricity and water bills over the life of the building."],
      ["Can you retrofit an existing house?", "Yes. We add solar, efficient lighting, automation and envelope upgrades without a full rebuild."],
    ],
  },
  emobility: {
    who: "Households, fleets and businesses switching from fuel vehicles to electric mobility.",
    get: ["Home and workplace charger design", "Load study so charging does not trip the supply", "Fleet electrification planning", "Installation and after-sales support", "Training for drivers and technicians"],
    specs: [
      ["Sites", "Homes, depots, offices and public parking"],
      ["Power", "Single-phase domestic to three-phase depot"],
      ["Option", "Solar-plus-storage to cut charging cost"],
    ],
    faqs: [
      ["Do I need a special meter?", "We check your supply first. Many homes use a dedicated circuit; larger sites may need an upgrade."],
      ["Can RENERG combine solar with charging?", "Yes. Solar plus storage can cut charging cost and keep vehicles moving during outages."],
    ],
  },
};

const COUNTRY_NOTES = {
  Rwanda: "Our home market — rooftop, pumping, commercial and public-sector plants delivered from Kigali.",
  Kenya: "Regional utility and institutional work, including large rooftops such as office campuses.",
  Tchad: "Hospital and critical-load solar where diesel is expensive and the grid is thin.",
  Canada: "A new RENERG market. Published Canada projects appear in the Projects page country filter automatically. Delivery follows the same engineering, procurement and commissioning standard as our East African plants, adapted to local codes and climate.",
  Tanzania: "Cross-border solar and pumping programmes with local installation under RENERG supervision.",
  Uganda: "Commercial and social-sector plants sized to load and tariff.",
  Burundi: "Compact hybrid systems for clinics, schools and businesses.",
  DRC: "Off-grid and hybrid plants for sites far from a stable grid.",
};

function fallbackServicePack() {
  return {
    who: "Clients who want a practical RENERG solution, not a generic catalogue.",
    get: ["Clear scope and budget", "Qualified installation team", "Documentation and aftercare"],
    specs: [["Start", "Share your site and load on the contact form"]],
    faqs: [["How do we start?", "Send the contact form with your site and load. We propose a visit and a written offer."]],
  };
}

export function ServiceExtras({ category, title }) {
  const pack = SERVICE_PACKS[String(category || "").toLowerCase()] || fallbackServicePack();

  return (
    <div className="mt-10 space-y-8">
      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl bg-teal-50 p-6">
          <h3 className="font-bold text-slate-900">Who this is for</h3>
          <p className="mt-2 text-slate-600">{pack.who}</p>
        </div>
        <div className="rounded-2xl bg-slate-50 p-6">
          <h3 className="font-bold text-slate-900">What RENERG delivers</h3>
          <ul className="mt-2 list-disc pl-5 text-slate-600 space-y-1">
            {pack.get.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      </div>
      {pack.specs?.length ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b bg-slate-50 px-6 py-3 font-bold text-slate-900">At a glance</div>
          <dl>
            {pack.specs.map(([k, v]) => (
              <div key={k} className="grid gap-1 border-t px-6 py-3 sm:grid-cols-3">
                <dt className="text-sm font-semibold text-teal-800">{k}</dt>
                <dd className="sm:col-span-2 text-slate-600">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="font-bold text-slate-900">Questions we hear about {title}</h3>
        <dl className="mt-4 space-y-4">
          {pack.faqs.map(([q, a]) => (
            <div key={q}>
              <dt className="font-semibold text-teal-800">{q}</dt>
              <dd className="mt-1 text-slate-600">{a}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="flex flex-wrap gap-3">
        <Link to="/contact" className="inline-flex rounded-xl bg-teal-600 px-5 py-3 font-semibold text-white">Request this service</Link>
        <Link to="/projects" className="inline-flex rounded-xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800">See related projects</Link>
      </div>
    </div>
  );
}

export function ProjectExtras({ country, title, capacity, location, status }) {
  const market = prettyCountry(country);
  const note = COUNTRY_NOTES[market] || `RENERG delivers in ${market} with the same quality as our Rwanda reference plants. New countries appear in the Projects country filter as soon as a project is published.`;

  return (
    <div className="mt-10 space-y-8">
      <div className="grid gap-5 md:grid-cols-3">
        <div className="rounded-2xl bg-teal-50 p-5">
          <p className="text-xs uppercase tracking-wide text-teal-700">Delivery</p>
          <p className="mt-1 font-semibold text-slate-900">RENERG design, supply and install</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 p-5">
          <p className="text-xs uppercase tracking-wide text-emerald-700">Impact</p>
          <p className="mt-1 font-semibold text-slate-900">{capacity ? `${capacity} of clean capacity` : "Lower diesel and grid cost"}</p>
        </div>
        <div className="rounded-2xl bg-slate-50 p-5">
          <p className="text-xs uppercase tracking-wide text-slate-500">Market</p>
          <p className="mt-1 font-semibold text-slate-900">{market}{location ? ` · ${location}` : ""}</p>
        </div>
      </div>
      <div className="rounded-2xl bg-white p-6 shadow-md border border-slate-100">
        <h3 className="renerg-h4 text-slate-900">Working in {market}</h3>
        <p className="mt-2 text-slate-600">{note}</p>
        <ol className="mt-4 list-decimal pl-5 space-y-2 text-slate-600">
          <li>Load and site data collected with the client{location ? ` in ${location}` : ""}.</li>
          <li>Engineering pack: arrays, structure, electrics and, where needed, storage.</li>
          <li>Procurement of bankable equipment and installation under RENERG supervision.</li>
          <li>Commissioning, training and a maintenance option so {title} keeps producing.</li>
        </ol>
        {status ? <p className="mt-4 text-sm text-slate-500">Current status: {String(status).replace(/_/g, " ")}</p> : null}
      </div>
      <div className="flex flex-wrap gap-3">
        <Link to="/contact" className="inline-flex rounded-xl bg-teal-600 px-5 py-3 font-semibold text-white">Ask for a similar project</Link>
        <Link to={`/projects/country/${encodeURIComponent(market)}`} className="inline-flex rounded-xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800">More in {market}</Link>
      </div>
    </div>
  );
}

export function countryNote(country) {
  const market = prettyCountry(country);
  return COUNTRY_NOTES[market] || `RENERG work in ${market} follows the same engineering, procurement and commissioning standard as our Rwanda plants.`;
}

export function CountryIntro({ country, count }) {
  const market = prettyCountry(country);
  return (
    <div className="mb-10 rounded-3xl bg-white p-7 shadow-lg">
      <p className="text-sm font-semibold uppercase tracking-wide text-teal-700">{count} case stud{count === 1 ? "y" : "ies"} to open</p>
      <h2 className="renerg-h3 mt-2 text-slate-900">Work in {market}</h2>
      <p className="mt-3 max-w-3xl text-slate-600">{countryNote(market)}</p>
      <p className="mt-4 text-sm text-slate-500">Photos and full scope sit inside each case study — pick one below to go further.</p>
    </div>
  );
}

export function CountryMarketGrid({ markets = [], large = false }) {
  if (!markets.length) return <p className="text-slate-500">No published markets yet.</p>;
  return (
    <div className={`grid gap-6 ${large ? "md:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
      {markets.map((market) => (
        <Link
          key={market.country}
          to={`/projects/country/${encodeURIComponent(market.country)}`}
          className="group relative block min-h-[280px] overflow-hidden rounded-3xl bg-slate-900 text-white shadow-xl"
        >
          {market.cover ? (
            <img
              src={mediaUrl(market.cover)}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-60 transition duration-700 group-hover:scale-105 group-hover:opacity-75"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-teal-800 to-slate-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
          <div className="relative flex h-full min-h-[280px] flex-col justify-end p-7">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-200">
              {market.count} project{market.count === 1 ? "" : "s"}
            </p>
            <h3 className="renerg-h3 mt-2">{market.country}</h3>
            {market.hint ? <p className="mt-2 text-sm text-teal-100/85">{market.hint}</p> : null}
            <span className="mt-5 inline-flex text-sm font-semibold text-white">
              Explore {market.country} →
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function CompanyExtras() {
  return (
    <div className="mt-12 grid gap-6 lg:grid-cols-3">
      <article className="rounded-3xl bg-white p-7 shadow-lg">
        <p className="text-xs uppercase tracking-wide text-teal-700">Experience</p>
        <h3 className="mt-2 text-xl font-bold">15+ years in solar and buildings</h3>
        <p className="mt-3 text-slate-600">From Kigali rooftops to hospital hybrids and UN-scale plants, RENERG stays on site through commissioning — not only on paper.</p>
      </article>
      <article className="rounded-3xl bg-white p-7 shadow-lg">
        <p className="text-xs uppercase tracking-wide text-teal-700">Markets</p>
        <h3 className="mt-2 text-xl font-bold">Africa first, new countries welcome</h3>
        <p className="mt-3 text-slate-600">Add a published project with country Canada (or any other name) in the dashboard and it appears in the Projects page country filter within seconds.</p>
      </article>
      <article className="rounded-3xl bg-white p-7 shadow-lg">
        <p className="text-xs uppercase tracking-wide text-teal-700">Promise</p>
        <h3 className="mt-2 text-xl font-bold">Honest scope, bankable kit</h3>
        <p className="mt-3 text-slate-600">We size to the real load, specify equipment we can support, and train the people who will live with the system.</p>
      </article>
    </div>
  );
}
